import { useEffect } from "react";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://mulebuyspreadsheets.monster";

export interface JsonLdObject {
  "@context": string;
  "@type": string;
  [key: string]: unknown;
}

interface PageMetaProps {
  title: string;
  description: string;
  jsonLd?: JsonLdObject[];
}

export default function PageMeta({ title, description, jsonLd }: PageMetaProps) {
  useEffect(() => {
    document.title = title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", description);
    }
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords && metaKeywords.getAttribute("data-dynamic") === "true") {
      metaKeywords.setAttribute("content", "");
    }
  }, [title, description]);

  if (!jsonLd || jsonLd.length === 0) return null;

  return (
    <>
      {jsonLd.map((item, index) => (
        <script
          key={`jsonld-${index}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(item, null, 2) }}
        />
      ))}
    </>
  );
}

export function buildArticleJsonLd(
  headline: string,
  description: string,
  slug: string,
  imageUrl?: string,
  datePublished?: string,
  dateModified?: string,
): JsonLdObject {
  const pubDate = datePublished || "2026-06-22";
  const modDate = dateModified || pubDate;
  const ld: JsonLdObject = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline,
    description,
    url: `${SITE_URL}${slug}`,
    datePublished: pubDate,
    dateModified: modDate,
    author: {
      "@type": "Organization",
      name: "Mulebuy Spreadsheet Hub",
    },
    publisher: {
      "@type": "Organization",
      name: "Mulebuy Spreadsheet Hub",
      url: SITE_URL,
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_URL}${slug}`,
    },
  };
  if (imageUrl) {
    (ld as Record<string, unknown>).image = imageUrl;
  }
  return ld;
}

export function buildFaqJsonLd(
  faqs: { question: string; answer: string }[],
): JsonLdObject {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

export { SITE_URL };